import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { salaryAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { KpiCard } from '@/components/KpiCard';
import { toast } from 'sonner';
import { Calculator, Wallet, Users, TrendingDown, Eye, Trash2, FileText } from 'lucide-react';

const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function SalaryPage() {
  const { user, isSuperUser } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) loadReport();
  }, [selectedBranch, selectedYear, selectedMonth]);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
      if (res.data.length > 0) {
        setSelectedBranch(user?.branch_id || res.data[0].id);
      }
    } catch (e) { console.error(e); }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await salaryAPI.report(selectedBranch, selectedYear, selectedMonth);
      if (res.data.results?.length > 0) {
        setResults(res.data);
      } else {
        setResults(null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await salaryAPI.calculate({
        branch_id: selectedBranch,
        year: selectedYear,
        month: selectedMonth
      });
      setResults(res.data);
      toast.success(`Salary calculated for ${months[selectedMonth - 1]} ${selectedYear}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleDeleteRun = async () => {
    try {
      await salaryAPI.deleteRun(selectedBranch, selectedYear, selectedMonth);
      toast.success(`Payroll deleted for ${months[selectedMonth - 1]} ${selectedYear}`);
      setResults(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Currency helpers
  const currentBranch = branches.find(b => b.id === selectedBranch);
  const currency = currentBranch?.currency || 'CAD';
  const currencySymbols = { INR: '₹', CAD: 'C$', USD: '$', GBP: '£', EUR: '€' };
  const sym = currencySymbols[currency] || currency;

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return `${sym}0`;
    return `${sym}${amount.toLocaleString('en-IN')}`;
  };

  const totalGross = results?.results?.reduce((s, r) => s + r.gross_salary, 0) || 0;
  const totalNet = results?.results?.reduce((s, r) => s + r.net_salary, 0) || 0;
  const totalDeductions = results?.results?.reduce((s, r) => s + r.deduction_amount, 0) || 0;

  const empTypeLabels = { permanent: 'Permanent', probation: 'Probation', contractor: 'Contractor' };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Salary Calculation</h1>
          <p className="text-muted-foreground">Monthly payroll based on attendance and deductions ({currency})</p>
        </div>
        <div className="flex gap-2">
          {results && isSuperUser && (
            <Button variant="outline" onClick={() => setDeleteDialogOpen(true)} className="gap-2 text-destructive" data-testid="salary-delete-run-button">
              <Trash2 size={16} /> Delete Run
            </Button>
          )}
          <Button onClick={handleCalculate} disabled={calculating || !selectedBranch} className="gap-2" data-testid="salary-run-payroll-button">
            <Calculator size={16} />
            {calculating ? 'Calculating...' : 'Run Payroll'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency || 'CAD'})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(Number(v))}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2025, 2026, 2027, 2028].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {results && results.results?.length > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard title="Working Days" value={results.working_days || 0} icon={Users} subtitle={`${results.holiday_count || 0} holidays`} />
            <KpiCard title="Total Gross" value={formatCurrency(totalGross)} icon={Wallet} />
            <KpiCard title="Total Deductions" value={formatCurrency(totalDeductions)} icon={TrendingDown} />
            <KpiCard title="Total Net" value={formatCurrency(totalNet)} icon={Calculator} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">
                Payroll - {months[selectedMonth - 1]} {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Days Present</TableHead>
                      <TableHead className="text-right">Lates</TableHead>
                      <TableHead className="text-right">Half Days</TableHead>
                      <TableHead className="text-right">Absent</TableHead>
                      <TableHead className="text-right">Deduction Days</TableHead>
                      <TableHead className="text-right">Gross ({sym})</TableHead>
                      <TableHead className="text-right">Deductions ({sym})</TableHead>
                      <TableHead className="text-right font-semibold">Net ({sym})</TableHead>
                      <TableHead>View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.results.map(r => (
                      <TableRow key={r.id || r.employee_id}>
                        <TableCell className="font-medium">{r.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {empTypeLabels[r.employment_type] || 'Permanent'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.days_present}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={r.total_lates > 0 ? 'text-amber-600 font-medium' : ''}>{r.total_lates}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.total_half_days}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.employment_type === 'contractor' ? (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          ) : r.days_absent}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.total_deduction_days?.toFixed(1)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(r.gross_salary)}</TableCell>
                        <TableCell className="text-right tabular-nums text-destructive">
                          {r.deduction_amount > 0 ? `-${formatCurrency(r.deduction_amount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(r.net_salary)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => setViewRecord(r)} data-testid={`salary-view-${r.id}`}>
                            <Eye size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!results && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Calculator className="mx-auto mb-3 text-muted-foreground/50" size={40} />
            <p>No salary data for this period. Click "Run Payroll" to calculate.</p>
          </CardContent>
        </Card>
      )}

      {/* View Detail Dialog */}
      <Dialog open={!!viewRecord} onOpenChange={(open) => !open && setViewRecord(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Salary Detail - {viewRecord?.employee_name}</DialogTitle>
          </DialogHeader>
          {viewRecord && (
            <div className="space-y-3">
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee</span>
                  <span className="font-medium">{viewRecord.employee_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline" className="capitalize text-xs">{empTypeLabels[viewRecord.employment_type] || 'Permanent'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Period</span>
                  <span>{months[viewRecord.month - 1]} {viewRecord.year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Working Days</span>
                  <span className="tabular-nums">{viewRecord.working_days}</span>
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-2 text-sm">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Attendance</p>
                <div className="flex justify-between"><span>Days Present</span><span className="tabular-nums">{viewRecord.days_present}</span></div>
                <div className="flex justify-between"><span>Approved Leaves</span><span className="tabular-nums">{viewRecord.approved_leaves}</span></div>
                <div className="flex justify-between"><span>Days Absent</span><span className="tabular-nums">{viewRecord.days_absent}</span></div>
                <div className="flex justify-between"><span>Total Lates</span><span className="tabular-nums text-amber-600">{viewRecord.total_lates}</span></div>
                <div className="flex justify-between"><span>Half Days</span><span className="tabular-nums">{viewRecord.total_half_days}</span></div>
              </div>

              <div className="rounded-lg border p-4 space-y-2 text-sm">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide">Salary Breakdown</p>
                <div className="flex justify-between"><span>Gross Salary</span><span className="tabular-nums font-medium">{formatCurrency(viewRecord.gross_salary)}</span></div>
                <div className="flex justify-between"><span>Late Deduction Days</span><span className="tabular-nums">{viewRecord.late_day_deductions}</span></div>
                <div className="flex justify-between"><span>Half Day Deductions</span><span className="tabular-nums">{viewRecord.half_day_deductions}</span></div>
                <div className="flex justify-between"><span>Total Deduction Days</span><span className="tabular-nums">{viewRecord.total_deduction_days?.toFixed(1)}</span></div>
                <div className="flex justify-between border-t pt-2"><span>Total Deductions</span><span className="tabular-nums text-destructive">-{formatCurrency(viewRecord.deduction_amount)}</span></div>
                <div className="flex justify-between border-t pt-2 font-semibold text-base">
                  <span>Net Salary</span>
                  <span className="tabular-nums">{formatCurrency(viewRecord.net_salary)}</span>
                </div>
              </div>

              {viewRecord.calculated_at && (
                <p className="text-xs text-muted-foreground text-center">
                  Calculated on {new Date(viewRecord.calculated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewRecord(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Run Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payroll Run</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the payroll run for <strong>{months[selectedMonth - 1]} {selectedYear}</strong>?
              This will remove all calculated salary records for this period. You can recalculate at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRun} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="salary-delete-confirm">
              Delete Run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
